import { Card, CardHeader, CardContent, Chip, Box, Typography, styled, alpha } from '@mui/material';

// Styled components for consistent styling
export const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  // Soft glass-like background with slight gradient for depth
  background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.85)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
  backdropFilter: 'blur(12px)',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
  },
}));

export const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  padding: theme.spacing(2.5, 3, 0, 3),
  '& .MuiCardHeader-avatar': {
    marginRight: theme.spacing(2),
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
  '& .MuiCardHeader-title': {
    fontSize: '1.15rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: '-0.015em',
    lineHeight: 1.4,
  },
  '& .MuiCardHeader-subheader': {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.25),
    letterSpacing: '0.01em',
  },
}));

export const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(2.5, 3, 3, 3),
  '&:last-child': {
    paddingBottom: theme.spacing(3),
  },
}));

export const MetricValue = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  letterSpacing: '-0.02em',
  lineHeight: 1.2,
  marginBottom: theme.spacing(0.5),
}));

export const MetricLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  color: theme.palette.text.secondary,
  fontWeight: 500,
  letterSpacing: '0.01em',
  textTransform: 'uppercase',
}));

export const MetricChange = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginTop: theme.spacing(1),
}));

// MetricChip
export const MetricChip = styled(Chip)(({ theme }) => ({
  height: 24,
  borderRadius: 12,
  fontWeight: 500,
  fontSize: '0.75rem',
  backgroundColor: alpha(theme.palette.primary.main, 0.06),
  color: theme.palette.primary.main,
  '&.MuiChip-colorSuccess': {
    backgroundColor: alpha(theme.palette.success.main, 0.06),
    color: theme.palette.success.main,
  },
  '&.MuiChip-colorError': {
    backgroundColor: alpha(theme.palette.error.main, 0.06),
    color: theme.palette.error.main,
  },
  '&.MuiChip-colorInfo': {
    backgroundColor: alpha(theme.palette.info.main, 0.06),
    color: theme.palette.info.main,
  },
  '& .MuiChip-label': {
    padding: '0 12px',
  },
}));

export const SummaryMetric = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.background.default, 0.6),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  minHeight: 100,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  },
}));

// Helper function to format display items
type DisplayItem = string | {
  score?: number;
  rationale?: string;
  text?: string;
  name?: string;
  value?: string | number;
  description?: string;
  [key: string]: unknown;
} | null | undefined;

export const formatDisplayItem = (item: DisplayItem): string => {
  // If item is already a string, just return it
  if (typeof item === 'string') {
    return item;
  }

  // Handle objects
  if (item && typeof item === 'object') {
    // Case: object has score and rationale properties (most common issue)
    if ('score' in item && 'rationale' in item) {
      return `${item.rationale} (Score: ${item.score})`;
    }

    // Case: object has other recognizable properties (add more as needed)
    if (item.text) return item.text;
    if (item.name) return item.name;
    if (item.value) return String(item.value); // Ensure value is stringified
    if (item.description) return item.description;

    // Fallback: If no recognizable property, stringify the object (truncated)
    try {
      const jsonString = JSON.stringify(item);
      return jsonString.length > 75 ? jsonString.substring(0, 72) + '...' : jsonString;
    } catch {
      return 'Complex Item'; // Final fallback
    }
  }

  // Handle null, undefined, boolean, or other types by converting to string
  return String(item ?? ''); // Use ?? for nullish coalescing
};